import React, { useEffect } from "react";
import { useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import Card from "../components/ui/Card";
import { SkeletonCard } from "../components/ui/Skeleton";
import { ErrorState } from "../components/ui/EmptyState";
import { formatCurrency, formatDate } from "../utils/dateUtils";
import { useDashboardSummary, useDashboardInvoices } from "../hooks/useDashboard";
import { useLanguage } from "../lang/LangContext";
import { KEYS } from "../lang/keys";import { useCurrency } from '../context/CurrencyContext';
const StatTile = ({ label, value, accentColor = 'border-accent', delay = 0 }) => (
    <div
        className="animate-appear"
        style={{ animationDelay: `${delay}ms` }}
    >
        <Card className={`border-l-4 ${accentColor}`}>
            <p className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1">
                {label}
            </p>
            <p className="text-2xl font-mono font-medium text-text-primary">
                {value}
            </p>
        </Card>
    </div>
);

const Home = () => {
    const { t } = useLanguage();
    const { currency } = useCurrency();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();

    // Category colors for spend breakdown (inside component to use translations)
    const categoryConfig = {
        electricity: { color: 'bg-category-electricity', label: t(KEYS.TYPE_ELECTRICITY) },
        natural_gas: { color: 'bg-category-gas', label: t(KEYS.TYPE_NATURAL_GAS) },
        water: { color: 'bg-category-water', label: t(KEYS.TYPE_WATER) },
        phone: { color: 'bg-category-phone', label: t(KEYS.TYPE_PHONE) },
        internet: { color: 'bg-category-internet', label: t(KEYS.TYPE_INTERNET) },
        other: { color: 'bg-category-other', label: t(KEYS.TYPE_OTHER) },
    };

    useEffect(() => {
        if (searchParams.get('verified') === '1') {
            queryClient.invalidateQueries({ queryKey: ['user'] });
            setSearchParams({}, { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const {
        data: summary,
        isLoading: summaryLoading,
        error: summaryError,
        refetch: refetchSummary,
    } = useDashboardSummary();

    const {
        data: invoiceData,
        isLoading: invoicesLoading,
        error: invoicesError,
        refetch: refetchInvoices,
    } = useDashboardInvoices({ status: 'unpaid', limit: 50, sort: 'due_date_asc' });

    const loading = summaryLoading || invoicesLoading;
    const error = summaryError || invoicesError;

    const aggregates = summary?.aggregates ?? {};
    const services = summary?.services ?? [];
    const invoices = invoiceData?.invoices ?? [];

    const totalServices = aggregates.total_services ?? 0;
    const suppliersCount = aggregates.total_suppliers ?? 0;
    const totalMonthlySpend = aggregates.current_month_spend ?? 0;
    const typesCount = new Set(services.map(s => s.type).filter(Boolean)).size;

    // Upcoming invoices — already filtered to unpaid + sorted asc by due_date by the API
    const upcomingInvoices = invoices.slice(0, 5);

    // Get invoice status
    const getStatus = (invoice) => {
        if (invoice.is_paid) return 'paid';
        const today = new Date(); today.setHours(0,0,0,0);
        const due = new Date(invoice.due_date); due.setHours(0,0,0,0);
        if (due < today) return 'overdue';
        const threeDays = new Date(today); threeDays.setDate(today.getDate() + 3);
        if (due <= threeDays) return 'pending';
        return 'upcoming';
    };

    const statusColors = {
        paid: 'text-status-paid',
        pending: 'text-status-pending',
        overdue: 'text-status-overdue',
        upcoming: 'text-status-upcoming',
    };

    // Spend by service type (from invoices, using enriched serviceType)
    const spendByCategory = invoices.reduce((acc, inv) => {
        const cat = (inv.serviceType || 'other').toLowerCase();
        acc[cat] = (acc[cat] || 0) + (parseFloat(inv.amount) || 0);
        return acc;
    }, {});
    const totalSpend = Object.values(spendByCategory).reduce((a, b) => a + b, 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-semibold text-text-primary">
                    {t(KEYS.HOME_TITLE)}
                </h1>
                <p className="text-sm text-text-muted mt-0.5">
                    {t(KEYS.HOME_SUBTITLE)}
                </p>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1,2,3,4].map(i => <SkeletonCard key={i} />)}
                </div>
            ) : error ? (
                <ErrorState message={error?.message || t(KEYS.HOME_ERROR_LOAD)} onRetry={() => { refetchSummary(); refetchInvoices(); }} />
            ) : (
                <>
                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatTile
                            label={t(KEYS.HOME_STAT_TOTAL_SERVICES)}
                            value={totalServices}
                            accentColor="border-l-accent"
                            delay={0}
                        />
                        <StatTile
                            label={t(KEYS.HOME_STAT_MONTHLY_SPEND)}
                            value={formatCurrency(totalMonthlySpend, currency)}
                            accentColor="border-l-status-paid"
                            delay={50}
                        />
                        <StatTile
                            label={t(KEYS.HOME_STAT_SUPPLIERS)}
                            value={suppliersCount}
                            accentColor="border-l-status-pending"
                            delay={100}
                        />
                        <StatTile
                            label={t(KEYS.HOME_STAT_SERVICE_TYPES)}
                            value={typesCount}
                            accentColor="border-l-status-upcoming"
                            delay={150}
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Upcoming Due */}
                        <div className="lg:col-span-2 animate-appear" style={{ animationDelay: '200ms' }}>
                            <Card>
                                <h2 className="text-sm font-semibold text-text-primary mb-4">
                                    {t(KEYS.HOME_UPCOMING_DUE)}
                                </h2>
                                {upcomingInvoices.length > 0 ? (
                                    <div className="space-y-2">
                                        {upcomingInvoices.map((inv) => {
                                            const status = getStatus(inv);
                                            return (
                                                <div
                                                    key={inv.uid}
                                                    className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-surface-overlay transition-colors"
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div className={`w-1.5 h-1.5 rounded-full ${status === 'overdue' ? 'bg-status-overdue' : status === 'pending' ? 'bg-status-pending' : 'bg-status-upcoming'}`} />
                                                        <span className="text-sm text-text-primary truncate">
                                                            {inv.serviceName || inv.name || 'Invoice'}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-4 shrink-0">
                                                        <span className="text-xs font-mono text-text-muted">
                                                            {formatDate(inv.due_date)}
                                                        </span>
                                                        <span className={`text-sm font-mono font-medium ${statusColors[status]}`}>
                                                            {formatCurrency(inv.amount, currency)}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-sm text-text-muted py-4 text-center">
                                        {t(KEYS.HOME_NO_UPCOMING_INVOICES)}
                                    </p>
                                )}
                            </Card>
                        </div>

                        {/* Spend by Category */}
                        <div className="animate-appear" style={{ animationDelay: '250ms' }}>
                            <Card>
                                <h2 className="text-sm font-semibold text-text-primary mb-4">
                                    {t(KEYS.HOME_OUTSTANDING_BY_CATEGORY)}
                                </h2>
                                {totalSpend > 0 ? (
                                    <div className="space-y-3">
                                        {/* Stacked bar */}
                                        <div className="flex h-2 rounded-full overflow-hidden bg-surface-overlay">
                                            {Object.entries(spendByCategory).map(([cat, amount]) => (
                                                <div
                                                    key={cat}
                                                    className={`${categoryConfig[cat]?.color || 'bg-category-other'}`}
                                                    style={{ width: `${(amount / totalSpend) * 100}%` }}
                                                />
                                            ))}
                                        </div>
                                        {/* Legend */}
                                        <div className="space-y-2">
                                            {Object.entries(spendByCategory).map(([cat, amount]) => (
                                                <div key={cat} className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-2 h-2 rounded-full ${categoryConfig[cat]?.color || 'bg-category-other'}`} />
                                                        <span className="text-xs text-text-secondary capitalize">
                                                            {categoryConfig[cat]?.label || cat}
                                                        </span>
                                                    </div>
                                                    <span className="text-xs font-mono text-text-muted">
                                                        {formatCurrency(amount, currency)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-text-muted py-4 text-center">
                                        {t(KEYS.HOME_NO_OUTSTANDING_INVOICES)}
                                    </p>
                                )}
                            </Card>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default Home;
